import { type ErrorEvent as SentryErrorEvent } from '@sentry/browser';

import {
	OBJECT_OBJECT,
	REBUILT_MESSAGE_MAX_LENGTH,
	REDACTION_RULES
} from 'src/common/services/SentryEventHelper.constants';

const safeStringify = (value: unknown): string => {
	try {
		return JSON.stringify(value);
	} catch {
		return String(value);
	}
};

const redactSecrets = (text: string): string =>
	REDACTION_RULES.reduce(
		(acc, { pattern, replacement }) => acc.replace(pattern, replacement),
		text
	);

export const isUninformativeText = (text: string | undefined | null): boolean =>
	!text || text.includes(OBJECT_OBJECT);

const renderConsoleArg = (arg: unknown): string => {
	if (typeof arg === 'string') {
		return arg;
	}

	if (arg instanceof Error) {
		return arg.message ? `${arg.name}: ${arg.message}` : arg.name;
	}

	if (arg && typeof arg === 'object') {
		const value = arg as Record<string, unknown>;
		const preferred =
			(typeof value.message === 'string' && value.message) ||
			(typeof value.exceptionDetails === 'string' && value.exceptionDetails) ||
			(typeof value.error === 'string' && value.error);

		if (preferred) {
			return preferred;
		}

		const json = safeStringify(arg);

		return json && json !== '{}' ? json : Object.prototype.toString.call(arg);
	}

	return String(arg);
};

export const rebuildMessageFromArguments = (event: SentryErrorEvent): string | undefined => {
	const args =
		event.extra && 'arguments' in event.extra ? (event.extra.arguments as unknown) : undefined;

	if (!Array.isArray(args) || args.length === 0) {
		return undefined;
	}

	const rendered = args.map(renderConsoleArg).filter(Boolean).join(' ').trim();

	if (isUninformativeText(rendered)) {
		return undefined;
	}

	const redacted = redactSecrets(rendered);

	return redacted.length > REBUILT_MESSAGE_MAX_LENGTH
		? `${redacted.slice(0, REBUILT_MESSAGE_MAX_LENGTH)}...`
		: redacted;
};

export const extractEventText = (event: SentryErrorEvent): string => {
	const messageText = typeof event.message === 'string' ? event.message : '';
	const exceptionText =
		event.exception?.values
			?.map((value) => value.value ?? '')
			.filter(Boolean)
			.join(' ') ?? '';
	const extraText =
		event.extra && 'arguments' in event.extra ? safeStringify(event.extra.arguments) : '';

	return `${messageText} ${exceptionText} ${extraText}`.trim();
};
